import type {
  ReactiveController,
  ReactiveControllerHost,
  ReactiveElement,
} from 'lit';

/**
 * The SlotHelper ReactiveController.
 *
 * This controller provides utilities for testing and managing slots in Web Components.
 * It allows you to:
 * - Test if a named slot is present/populated
 * - Test if the default slot is present/populated
 * - Get a list of all available slots
 *
 * @example
 * // Initialize the SlotHelper in your Web Component
 * class MyElement extends LitElement {
 *   slots = new SlotHelper(this);
 *
 *   // Use the SlotHelper methods in your component's logic
 *   render() {
 *     return html`
 *       ${this.slots.isNotEmpty('example-slot') ?
 *          html`<div class="example-slot--wrapper">
 *            <slot name="example-slot"></slot>
 *          </div>` :
 *        nothing }
 *      `
 *   }
 * }
 *
 * Methods:
 * - isNotEmpty(slotName?: string): boolean
 *   Tests if a slot is present and populated.
 *   If no slotName is provided, it tests the default slot.
 *
 * - getSlotNames(): string[]
 *   Returns an array of all available slot names.
 *
 * Properties:
 * - slotNames: string[]
 *   An array containing the names of all available slots.
 *
 * - hasDefaultSlotContent: boolean
 *   Indicates whether the host element has default slot content.
 *
 * - hasNamedSlotContent: boolean
 *   Indicates whether the host element has named slot content.
 *
 * Usage examples:
 *
 * 1. Test if a specific named slot exists:
 *    if (this.slots.isNotEmpty('header')) {
 *      console.log('Header slot exists and is populated');
 *    }
 *
 * 2. Test if the default slot exists:
 *    if (this.slots.isNotEmpty()) {
 *      console.log('Default slot exists and is populated');
 *    }
 *
 * 3. Get all available slot names:
 *    const availableSlots = this.slots.getSlotNames();
 *    console.log('Available slots:', availableSlots);
 *
 *
 * Note: The SlotHelper automatically updates when slot content changes,
 * ensuring that your component always has the latest slot information.
 *
 * ⠀⠀⠀⠀⠀⠀⣠⣞⠙⡶⢄⣠⡞⠋⠳⡶⢤⡀⠀⣠⡴⠦⣄⣀⡴⣶⠤⡄⠀⠀
 * ⠀⠀⠀⠀⠀⣰⡏⠘⣆⡗⣸⡟⠀⠀⠀⣿⢮⣧⣰⠋⠀⠀⠸⣝⣶⢡⠔⠻⡆⠀
 * ⠀⠀⠀⠀⠀⣿⠀⠀⠛⢳⠋⡇⠀⠀⠀⢩⠻⠋⡏⠀⠀⠀⠀⡿⡿⠃⠀⠀⢹⡀
 * ⠀⠀⠀⠀⠀⡟⠀⠀⠀⢸⠀⣷⠀⠀⠀⠸⡀⠈⣧⠀⠀⠀⠀⣇⢻⠀⠀⠀⠘⡇
 * ⠀⢀⣠⡴⢶⠟⠶⢦⣄⠀⢇⢸⡄⠀⠀⠀⢡⡀⢻⡀⠀⠀⠀⠸⣿⠀⠀⠀⠀⣿
 * ⢠⣿⠝⠉⠁⠀⠺⣧⠹⢧⣬⡄⣇⠀⠀⠀⠀⢧⠘⣇⠀⠀⠀⠀⠹⡄⠀⠀⠀⣿
 * ⡞⣁⣤⠀⠰⢟⡰⠂⠀⢸⠻⢦⡏⠀⠀⠀⢀⠸⡤⢿⡄⠀⠀⠀⠀⠹⣆⠀⠀⣿
 * ⢷⠁⢹⠉⠉⠉⠀⢀⠤⢃⡠⠊⠀⠀⠀⢠⠈⡗⠃⢸⠃⠀⠀⠀⠀⠀⠸⡄⢀⡟
 * ⠘⣧⣇⣀⣀⠤⠐⠁⠀⠀⠀⠀⠀⠀⠑⠺⢿⠁⠀⠎⠀⠀⠀⠀⠀⠀⠀⠁⣸⠃
 * ⠀⠘⣧⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢰⡏⠀
 * ⠀⠀⠈⢿⣦⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣰⠟⠀⠀
 * ⠀⠀⠀⠀⠈⠻⣷⣤⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⣠⡼⠋⠀⠀⠀
 * ⠀⠀⠀⠀⠀⠀⠀⠉⠙⠻⠶⢶⣤⣤⣤⣤⣤⣤⣤⣤⣴⠶⠞⠋⠁⠀⠀⠀⠀⠀
 *
 */
export class SlotHelper implements ReactiveController {
  host: ReactiveControllerHost & Element;
  hostEl: ReactiveElement;
  slotNames: string[] = [];
  hasDefaultSlotContent: boolean;
  hasNamedSlotContent: boolean;
  private debug: boolean;

  constructor(host: ReactiveControllerHost & Element, debug = false) {
    (this.host = host).addController(this);
    this.hostEl = this.host as unknown as ReactiveElement;
    this.handleSlotChange = this.handleSlotChange.bind(this);
    this.debug = debug;
  }

  /**
   * Logs debug messages if debug mode is enabled
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[SlotHelper] ${message}`, ...args);
    }
  }

  /**
   * Checks if the host element has content in its default slot.
   * @private
   * @returns {boolean} True if the default slot contains content, false otherwise.
   */
  private hasDefaultSlot() {
    this.debugLog('Checking default slot content');

    for (const node of this.host.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent!.trim() !== '') {
        this.debugLog('Found text content in default slot');
        return (this.hasDefaultSlotContent = true);
      }
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        !(node as Element).hasAttribute('slot')
      ) {
        this.debugLog('Found element content in default slot', { node });
        return (this.hasDefaultSlotContent = true);
      }
    }

    this.debugLog('No content found in default slot');
    return (this.hasDefaultSlotContent = false);
  }

  /**
   * Checks if the host element has a named slot.
   * @private
   * @param {string} name - The name of the slot to check for.
   * @returns {boolean} True if the named slot is found, false otherwise.
   */
  private hasNamedSlot(name: string) {
    this.debugLog('Checking named slot', { name });

    const slotElement = this.host.querySelector(`:scope > [slot="${name}"]`);
    if (slotElement) {
      this.debugLog('Found slot element', {
        name,
        element: slotElement,
        hasChildren: slotElement.children.length > 0,
        innerHTML: slotElement.innerHTML,
      });
      return (this.hasNamedSlotContent = true);
    }

    this.debugLog('No slot element found', { name });
    return (this.hasNamedSlotContent = false);
  }

  /**
   * Tests for the presence of a slot.
   * @param {string} [slotName] - The name of the slot to test. If undefined, tests for the default slot.
   * @returns {boolean} True if the slot exists and is populated, false otherwise.
   */
  isNotEmpty(slotName?: string | undefined) {
    this.debugLog('Testing slot presence', { slotName });
    const result =
      slotName === undefined
        ? this.hasDefaultSlot()
        : this.hasNamedSlot(slotName);

    this.debugLog('Slot presence test result', { slotName, result });
    return result;
  }

  /**
   * Lifecycle method called when the host element is connected to the DOM.
   * Sets up the slotchange event listener and initializes the slot names.
   */
  hostConnected() {
    this.debugLog('Host connected to DOM');
    this.host.shadowRoot!.addEventListener('slotchange', this.handleSlotChange);
    this.slotNames = this.getSlotNames();
    this.debugLog('Initial slot names', { slotNames: this.slotNames });
  }

  /**
   * Lifecycle method called when the host element is disconnected from the DOM.
   * Removes the slotchange event listener.
   */
  hostDisconnected() {
    this.debugLog('Host disconnected from DOM');
    this.host.shadowRoot!.removeEventListener(
      'slotchange',
      this.handleSlotChange
    );
  }

  /**
   * Handles the slotchange event.
   * @param {Event} event - The slotchange event object.
   */
  handleSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement;
    this.debugLog('Slot change detected', {
      slotName: slot.name,
      assignedNodes: slot.assignedNodes().length,
    });

    // Update slotNames in case slot content changes.
    this.slotNames = this.getSlotNames();
    this.debugLog('Updated slot names', { slotNames: this.slotNames });

    if (
      (this.hasDefaultSlotContent && !slot.name) ||
      (slot.name && this.slotNames.includes(slot.name))
    ) {
      this.debugLog('Requesting update due to slot change');
      this.host.requestUpdate();
    }
  }

  /**
   * Returns an array of slot names present in the host element,
   * including "default slot" if a default slot is present.
   * @returns {string[]} An array of slot names.
   */
  getSlotNames() {
    this.debugLog('Getting slot names');
    const slots: string[] = [];
    const namedSlotElements = this.host.querySelectorAll('[slot]');
    this.hasNamedSlotContent = namedSlotElements.length > 0;

    this.debugLog('Found named slot elements', {
      count: namedSlotElements.length,
      elements: Array.from(namedSlotElements).map(el => ({
        slot: el.getAttribute('slot'),
        hasContent: el.children.length > 0,
      })),
    });

    namedSlotElements.forEach((slot: Element) => {
      const name = slot.getAttribute('slot');
      if (name && !slots.includes(name)) {
        slots.push(name);
      }
    });

    // Check for default slot
    if (this.hasDefaultSlot() && !slots.includes('default slot')) {
      slots.unshift('default slot');
    }

    this.debugLog('Final slot names', { slots });
    return slots;
  }
}
